import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Loader2, ShieldCheck } from "lucide-react";
import { PERSONAS, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { MemberAuthForm, memberApi } from "./MembershipPage";

const STATUS_STYLE = {
  active: "border-jawa-turquoise/60 bg-jawa-turquoise/10 text-jawa-espresso",
  pending: "border-jawa-gold/60 bg-jawa-gold/10 text-jawa-espresso",
  expired: "border-jawa-espresso/20 bg-jawa-stone/50 text-jawa-espresso/60",
};

export default function MembersPage() {
  const [member, setMember] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    const token = localStorage.getItem("jawa_member_token");
    if (!token) {
      setLoaded(true);
      return;
    }
    memberApi()
      .get("/members/me")
      .then((res) => setMember(res.data))
      .catch(() => localStorage.removeItem("jawa_member_token"))
      .finally(() => setLoaded(true));
  };

  useEffect(load, []);

  const logout = () => {
    localStorage.removeItem("jawa_member_token");
    setMember(null);
  };

  return (
    <div className="bg-jawa-ivory" data-testid="members-page">
      <Nav />
      <main className="mx-auto max-w-4xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="Member area" title="Your JÀWÀ membership" />
        </FadeUp>

        {!loaded ? (
          <p className="mt-16 flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-jawa-espresso/50">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : !member ? (
          <FadeUp className="mx-auto mt-12 max-w-md border border-jawa-stone bg-white p-8">
            <MemberAuthForm onAuthed={() => load()} />
          </FadeUp>
        ) : (
          <FadeUp className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-jawa-espresso/70">
                Signed in as <span className="font-semibold text-jawa-espresso">{member.name}</span> ({member.email})
              </p>
              <button
                onClick={logout}
                data-testid="member-logout"
                className="text-xs uppercase tracking-[0.2em] text-jawa-espresso/50 transition-colors hover:text-jawa-coral"
              >
                Sign out
              </button>
            </div>

            <div className="mt-8 space-y-4" data-testid="membership-list">
              {member.memberships.length === 0 && (
                <div className="border border-jawa-stone bg-white p-8 text-sm text-jawa-espresso/60" data-testid="no-memberships">
                  No memberships yet — choose yours below.
                </div>
              )}
              {member.memberships.map((m) => {
                const expired = m.status === "active" && new Date(m.end_date) <= new Date();
                const status = expired ? "expired" : m.status;
                return (
                  <div key={m.id} className={`border p-6 ${STATUS_STYLE[status]}`} data-testid={`membership-${m.reference}`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="font-serif text-xl font-medium">
                          JÀWÀ {m.audience.charAt(0).toUpperCase() + m.audience.slice(1)} Membership
                          <span className="ml-3 align-middle text-[10px] uppercase tracking-[0.2em] opacity-70">
                            {m.period} · {m.currency} {m.amount}
                          </span>
                        </p>
                        <p className="mt-1 font-mono text-xs opacity-70">Ref {m.reference}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold uppercase tracking-[0.25em]">{status}</p>
                        {m.end_date && (
                          <p className="mt-1 text-xs opacity-70">
                            {status === "active" ? "until" : "ended"} {new Date(m.end_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      {status === "active" && (
                        <Link
                          to={`/members/guides/${m.audience}`}
                          data-testid={`open-guide-${m.audience}`}
                          className="flex items-center gap-1.5 bg-jawa-espresso px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory hover:bg-jawa-terracotta"
                        >
                          <BookOpen className="h-4 w-4" /> Open your A–Z collection
                        </Link>
                      )}
                      {(status === "active" || expired) && (
                        <a
                          href={waLink(
                            `Hello JÀWÀ — I would like to RENEW my ${m.audience} membership (${m.period}, ${m.currency} ${m.amount}).\nEmail: ${m.member_email}\nReference: ${m.reference}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`renew-${m.audience}`}
                          className="flex items-center gap-1.5 border border-jawa-espresso/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso hover:border-jawa-turquoise"
                        >
                          Renew via WhatsApp <ArrowUpRight className="h-4 w-4" />
                        </a>
                      )}
                      {status === "pending" && (
                        <a
                          href={waLink(
                            `Hello JÀWÀ — I would like to request payment details for the ${m.audience} membership (${m.period}, ${m.currency} ${m.amount}).\nEmail: ${m.member_email}\nReference: ${m.reference}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`pay-request-${m.audience}`}
                          className="flex items-center gap-1.5 bg-jawa-turquoise px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-nearblack hover:bg-jawa-turquoise/90"
                        >
                          Request Payment Details <ArrowUpRight className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-xs leading-relaxed text-jawa-espresso/50">
              <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-jawa-turquoise" />
              Memberships do not renew automatically — request a renewal before your expiry date to keep access.
            </p>

            <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="membership-picker">
              {Object.entries(PERSONAS).map(([id, p]) => (
                <Link
                  key={id}
                  to={`/membership/${id}`}
                  data-testid={`pick-membership-${id}`}
                  className="group border border-jawa-stone bg-white p-6 transition-colors hover:border-jawa-coral"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-coral">{p.kicker}</p>
                  <p className="mt-2 font-serif text-xl font-medium text-jawa-espresso group-hover:text-jawa-coral">
                    JÀWÀ {p.title.replace("The ", "")} Membership
                  </p>
                </Link>
              ))}
            </div>
          </FadeUp>
        )}
      </main>
      <Footer />
    </div>
  );
}
